import React from 'react';
import {
  AlertTriangle,
  Calendar,
  Clipboard,
  ExternalLink,
  Hash,
  MapPin,
  Phone,
  User,
  Wallet,
} from 'lucide-react';
import { compactText, formatDate, formatMoney, getTypeStyle } from './searchConfig';

function DetailChip({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600">
      <Icon className="w-3.5 h-3.5" />
      {children}
    </span>
  );
}

function copyValue(value) {
  if (!value) return;
  navigator.clipboard?.writeText(String(value));
}

function openWhatsApp(phone) {
  if (!phone) return;
  const digits = String(phone).replace(/\D/g, '');
  const normalized = digits.startsWith('51') ? digits : `51${digits}`;
  window.open(`https://wa.me/${normalized}`, '_blank', 'noopener,noreferrer');
}

function getDateLabel(result) {
  const dates = result.dates || {};
  return (
    formatDate(dates.delivery_date) ||
    formatDate(dates.pickup_date) ||
    formatDate(dates.scheduled_date) ||
    formatDate(dates.created_at)
  );
}

function getMoneyLabel(result) {
  const money = result.money || {};
  return (
    formatMoney(money.total_price) ||
    formatMoney(money.charge) ||
    formatMoney(money.charged) ||
    formatMoney(money.deposit_advance)
  );
}

function getAddress(result) {
  const location = result.location || {};
  return (
    location.address ||
    location.dropoff_address ||
    location.agency_address ||
    location.pickup_address
  );
}

function ResultCard({ result }) {
  const style = getTypeStyle(result.type);
  const Icon = style.icon;
  const dateLabel = getDateLabel(result);
  const moneyLabel = getMoneyLabel(result);
  const address = getAddress(result);
  const trackingCode = result.meta?.tracking_code || result.title;

  return (
    <article className={`bg-white border ${style.border} rounded-lg shadow-sm hover:shadow-md transition-shadow`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${style.bg} ${style.color}`}>
            <Icon className="w-5 h-5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs font-bold uppercase tracking-wide ${style.color}`}>
                {result.type_label || style.label}
              </span>
              <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-600">
                <Hash className="w-3 h-3" />
                {result.id}
              </span>
              {result.status && (
                <span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white">
                  {result.status}
                </span>
              )}
            </div>

            <h2 className="mt-1 truncate text-base font-bold text-slate-900 font-montserrat">
              {compactText(result.title)}
            </h2>
            <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">
              {compactText(result.subtitle)}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <DetailChip icon={User}>
            {result.owner?.name ? `${result.owner.name} (${result.owner.id})` : null}
          </DetailChip>
          <DetailChip icon={Phone}>{result.primary_phone}</DetailChip>
          <DetailChip icon={MapPin}>{address}</DetailChip>
          <DetailChip icon={Calendar}>{dateLabel}</DetailChip>
          <DetailChip icon={Wallet}>{moneyLabel}</DetailChip>
        </div>

        {result.driver && (
          <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Driver: <span className="font-semibold text-slate-800">{result.driver.name || result.driver.id}</span>
            {result.driver.phone ? ` - ${result.driver.phone}` : ''}
          </div>
        )}

        {result.flags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {result.flags.map((flag) => (
              <span
                key={flag}
                className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {flag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => copyValue(result.id)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <Clipboard className="w-3.5 h-3.5" />
            Copiar ID
          </button>

          {trackingCode && result.type !== 'user' && (
            <button
              type="button"
              onClick={() => copyValue(trackingCode)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              <Clipboard className="w-3.5 h-3.5" />
              Tracking
            </button>
          )}

          {result.primary_phone && (
            <button
              type="button"
              onClick={() => openWhatsApp(result.primary_phone)}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              WhatsApp
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default ResultCard;
